import { useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const findMatchingRouteStyle = (pathname, routeStyles) => {
  if (routeStyles[pathname]) {
    return routeStyles[pathname];
  }

  const matchingRoute = Object.keys(routeStyles).find(route => {
    if (route === '/') return false;

    return pathname.startsWith(route + '/') || pathname === route;
  });

  if (matchingRoute) {
    return routeStyles[matchingRoute];
  }

  return routeStyles['/'];
};

export const useShadowDOMStyling = (config = {}, routeStyles = {}) => {
  const location = useLocation();
  const intervalRef = useRef(null);
  const appliedRef = useRef(false);

  const {
    shadowRootId,
    selector,
    maxRetries = 30,
    retryInterval = 100
  } = config;

  useEffect(() => {
    appliedRef.current = false;
    let retryCount = 0;

    const applyShadowStyles = () => {
      const container = document.getElementById(shadowRootId);

      if (!container?.shadowRoot) {
        if (retryCount < maxRetries) {
          retryCount++;
          return false;
        }
        console.warn(`Shadow DOM not found after ${maxRetries} retries`);
        return true;
      }

      const element = container.shadowRoot.querySelector(selector);
      if (!element) {
        console.warn(`Element ${selector} not found in shadow DOM`);
        return true;
      }

      const styles = findMatchingRouteStyle(location.pathname, routeStyles);

      Object.assign(element.style, styles);
      Object.assign(element.style, {
        backgroundColor: '#000000',
        background: 'repeating-linear-gradient(-45deg, #666666, #666666 5px, #000000 5px, #000000 10px)',
        borderRadius: '6px',
        border: '0',
        borderBottomRightRadius: '0px',
        borderTopRightRadius: '0px',
        margin: '0px',
        height: '40px',
      });

      appliedRef.current = true;
      return true;
    };

    const startPolling = () => {
      if (appliedRef.current) return;

      intervalRef.current = setInterval(() => {
        const shouldStop = applyShadowStyles();
        if (shouldStop) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }, retryInterval);
    };

    const immediate = applyShadowStyles();
    if (!immediate) {
      startPolling();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [location.pathname, shadowRootId, selector, maxRetries, retryInterval, routeStyles]);
};